/**
 * Reusable chart helper components for the Results page.
 */
import { OPTION_COLORS } from './constants';
import { useTranslation } from 'react-i18next';

export const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    if (payload.length > 1) {
      const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
      return (
        <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-600 rounded-lg px-4 py-3 shadow-xl">
          <p className="text-white font-semibold mb-2 border-b border-slate-600 pb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 py-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.fill || entry.color || '#6366f1' }} />
              <span className="text-slate-300 text-sm">{entry.name}:</span>
              <span className="font-mono font-bold text-white">{entry.value}</span>
            </div>
          ))}
          <div className="border-t border-slate-600 mt-2 pt-2 flex justify-between">
            <span className="text-slate-400 text-sm">{t('results.total')}:</span>
            <span className="font-mono font-bold text-white">{total}</span>
          </div>
        </div>
      );
    }
    const entry = payload[0];
    const displayName = entry.name || label || entry.payload?.name;
    const displayValue = entry.value ?? entry.payload?.value;
    const color = entry.payload?.fill || entry.color || entry.fill || '#6366f1';
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-600 rounded-lg px-4 py-3 shadow-xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color.includes?.('url') ? '#6366f1' : color }} />
          <p className="text-white font-semibold">{displayName}</p>
        </div>
        <p className="text-slate-300 text-sm">
          {t('chart.votes')}: <span className="font-mono font-bold text-white">{displayValue}</span>
        </p>
        {entry.payload?.percentage !== undefined && (
          <p className="text-slate-400 text-xs">{entry.payload.percentage}% {t('chart.ofTotal')}</p>
        )}
      </div>
    );
  }
  return null;
};

export const Bar3D = (props) => {
  const { x, y, width, height, fill } = props;
  if (!height || height <= 0) return null;
  const depth = 12;
  const darkerFill = fill?.replace(/^#/, '') || '3b82f6';
  const r = parseInt(darkerFill.substr(0, 2), 16) || 59;
  const g = parseInt(darkerFill.substr(2, 2), 16) || 130;
  const b = parseInt(darkerFill.substr(4, 2), 16) || 246;
  const darker = `rgb(${Math.max(0, r - 50)}, ${Math.max(0, g - 50)}, ${Math.max(0, b - 50)})`;
  const lighter = `rgb(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 40)})`;
  return (
    <g>
      <polygon points={`${x},${y} ${x + depth},${y - depth} ${x + width + depth},${y - depth} ${x + width},${y}`} fill={lighter} stroke={darker} strokeWidth={0.5} />
      <polygon points={`${x + width},${y} ${x + width + depth},${y - depth} ${x + width + depth},${y + height - depth} ${x + width},${y + height}`} fill={darker} stroke={darker} strokeWidth={0.5} />
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke={darker} strokeWidth={0.5} />
    </g>
  );
};

export const Bar3DHorizontal = (props) => {
  const { x, y, width, height, fill } = props;
  if (!width || width <= 0) return null;
  const depth = 8;
  const darkerFill = fill?.replace(/^#/, '') || '3b82f6';
  const r = parseInt(darkerFill.substr(0, 2), 16) || 59;
  const g = parseInt(darkerFill.substr(2, 2), 16) || 130;
  const b = parseInt(darkerFill.substr(4, 2), 16) || 246;
  const darker = `rgb(${Math.max(0, r - 50)}, ${Math.max(0, g - 50)}, ${Math.max(0, b - 50)})`;
  const lighter = `rgb(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 40)})`;
  return (
    <g>
      <polygon points={`${x},${y} ${x + depth},${y - depth} ${x + width + depth},${y - depth} ${x + width},${y}`} fill={lighter} stroke={darker} strokeWidth={0.5} />
      <polygon points={`${x + width},${y} ${x + width + depth},${y - depth} ${x + width + depth},${y + height - depth} ${x + width},${y + height}`} fill={darker} stroke={darker} strokeWidth={0.5} />
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke={darker} strokeWidth={0.5} />
    </g>
  );
};

export const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: '13px', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const renderLegend = (props) => {
  const { payload } = props;
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-4">
      {payload.filter(entry => entry.value !== 'total').map((entry, index) => {
        const color = entry.payload?.fill || entry.color || OPTION_COLORS[index % OPTION_COLORS.length];
        const displayColor = color.includes('url') ? OPTION_COLORS[index % OPTION_COLORS.length] : color;
        return (
          <div key={index} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ background: displayColor, boxShadow: '2px 2px 4px rgba(0,0,0,0.2)' }} />
            <span className="text-slate-700 text-sm font-medium">{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
};
